import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Search, MapPin, User, Calendar, X, History, TrendingUp,
  Bell, Check, ChevronDown, ChevronUp, SlidersHorizontal,
  GraduationCap, Users, BookOpen, LayoutGrid, RotateCcw,
} from 'lucide-react';
import { usePrograms, useStoreActions, useSettingsStore, useUserPreferences } from '@/store/useStore';
import { UnifiedProgram } from '@/types';
import { DataProcessor } from '@/utils/dataProcessor';
import { compareTimeStrings } from '@/utils/timeUtils';
import { getOrderedDays } from '@/utils/dayUtils';
import Logo from '@/components/Logo';
import { DesktopNav } from '@/components/DesktopNav';
import { useDashboard } from '@/contexts/DashboardContext';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// ─── Design tokens (mirrors mobile home) ──────────────────────────────────────
const T = {
  bg:         '#0A0A0A',
  surface:    'rgba(255,255,255,.03)',
  surfaceHov: 'rgba(255,255,255,.055)',
  border:     'rgba(255,255,255,.07)',
  borderHov:  'rgba(255,255,255,.13)',
  teal:       '#14B8A6',
  tealBg:     'rgba(20,184,166,.1)',
  tealBorder: 'rgba(20,184,166,.22)',
  text:       '#fff',
  text50:     'rgba(255,255,255,.5)',
  text35:     'rgba(255,255,255,.35)',
  text25:     'rgba(255,255,255,.25)',
  text15:     'rgba(255,255,255,.15)',
  mono:       "'JetBrains Mono', monospace",
  serif:      "'Instrument Serif', serif",
  sans:       "'Outfit', sans-serif",
};

// ─── Primitives ───────────────────────────────────────────────────────────────
function MonoLabel({ children, color = T.text25, size = 9 }: { children: React.ReactNode; color?: string; size?: number }) {
  return (
    <span style={{
      fontFamily: T.mono, fontSize: size, letterSpacing: '.14em',
      color, textTransform: 'uppercase' as const,
    }}>{children}</span>
  );
}

function SectionHeader({ title, meta }: { title: string; meta?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <span style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, letterSpacing: '-.01em' }}>
        {title}
      </span>
      {meta && <MonoLabel>{meta}</MonoLabel>}
    </div>
  );
}

function NavIconBtn({ children, onClick, badge }: { children: React.ReactNode; onClick?: () => void; badge?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: hov ? 'rgba(255,255,255,.09)' : 'rgba(255,255,255,.05)',
        border: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : T.text50,
        cursor: 'pointer', transition: '.2s', position: 'relative',
      }}
    >
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          width: 14, height: 14, borderRadius: '50%',
          background: '#EF4444', border: `2px solid ${T.bg}`,
          fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

function ActiveChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };
  
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '5px 10px', borderRadius: 20,
      background: T.tealBg, border: `1px solid ${T.tealBorder}`,
      fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em', color: T.teal,
    }}>
      {label}
      <button onClick={() => { onRemove(); triggerHaptic(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.teal, display: 'flex', alignItems: 'center', padding: 0 }}>
        <X size={10} />
      </button>
    </div>
  );
}

// Combobox-style filter row used inside the filter sheet
function FilterRow({
  icon: Icon, label, value, options, onChange, searchPlaceholder,
}: {
  icon: any; label: string; value: string; options: string[];
  onChange: (v: string) => void; searchPlaceholder: string;
}) {
  const [open, setOpen] = useState(false);
  const active = value !== 'all';
  
  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '12px 14px', borderRadius: 10,
            background: active ? T.tealBg : T.surface,
            border: `1px solid ${active ? T.tealBorder : T.border}`,
            color: T.text, fontFamily: T.sans, fontSize: 13,
            cursor: 'pointer', transition: '.15s', textAlign: 'left',
          }}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: active ? 'rgba(20,184,166,.18)' : 'rgba(255,255,255,.04)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: active ? T.teal : T.text35,
          }}>
            <Icon size={13} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', color: active ? T.teal : T.text25, textTransform: 'uppercase' as const, marginBottom: 2 }}>
              {label}
            </div>
            <div style={{
              fontSize: 13, color: active ? T.text : T.text50,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {active ? value : 'Any'}
            </div>
          </div>
          {active ? (
            <X
              size={14}
              color={T.teal}
              onClick={(e) => { e.stopPropagation(); onChange('all'); triggerHaptic(); }}
              style={{ flexShrink: 0 }}
            />
          ) : (
            <ChevronDown size={14} color={T.text35} style={{ flexShrink: 0 }} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        style={{
          background: '#141414', border: `1px solid ${T.borderHov}`,
          borderRadius: 10, padding: 0, width: 'var(--radix-popover-trigger-width)',
          maxWidth: '92vw',
        }}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="all" onSelect={() => { onChange('all'); setOpen(false); triggerHaptic(); }}>
                <Check size={14} style={{ marginRight: 8, opacity: value === 'all' ? 1 : 0 }} />All
              </CommandItem>
              {options.map(opt => (
                <CommandItem key={opt} value={opt} onSelect={v => { onChange(v); setOpen(false); triggerHaptic(); }}>
                  <Check size={14} style={{ marginRight: 8, opacity: value === opt ? 1 : 0 }} />{opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SegmentedFilter({ value, options, onChange }: {
  value: string; options: { v: string; label: string }[]; onChange: (v: string) => void;
}) {
  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };
  
  return (
    <div style={{
      display: 'flex', background: T.surface,
      border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, gap: 4,
    }}>
      {options.map(({ v, label }) => {
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => { onChange(v); triggerHaptic(); }}
            style={{
              flex: 1, padding: '8px 0', borderRadius: 7,
              background: active ? T.tealBg : 'none',
              border: `1px solid ${active ? T.tealBorder : 'transparent'}`,
              color: active ? T.teal : T.text50,
              fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em',
              cursor: 'pointer', transition: '.15s',
            }}
          >{label}</button>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BrowseTimetables() {
  const dashboard       = useDashboard();
  const location        = useLocation();
  const userPreferences = useUserPreferences();
  const programs        = usePrograms();
  const { initializeData } = useStoreActions();

  const [searchQuery,          setSearchQuery]          = useState('');
  const [showSearchSuggestions,setShowSearchSuggestions]= useState(false);
  const [searchSuggestions,    setSearchSuggestions]    = useState<any[]>([]);
  const [recentSearches,       setRecentSearches]       = useState<string[]>([]);
  const [selectedCategory,     setSelectedCategory]     = useState('all');
  const [selectedProgram,      setSelectedProgram]      = useState('all');
  const [selectedTeacher,      setSelectedTeacher]      = useState('all');
  const [selectedCourse,       setSelectedCourse]       = useState('all');
  const [selectedSemester,     setSelectedSemester]     = useState('all');
  const [selectedGroup,        setSelectedGroup]        = useState('all');
  const [selectedTimetable,    setSelectedTimetable]    = useState<UnifiedProgram | null>(null);
  const [showDetails,          setShowDetails]          = useState(false);
  const [selectedDayView,      setSelectedDayView]      = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [viewMode,             setViewMode]             = useState<'day' | 'week'>('day');
  const [showFilters,          setShowFilters]          = useState(false);
  const [searchFocused,        setSearchFocused]        = useState(false);
  const [isLoading,            setIsLoading]            = useState(true);
  const [isRefreshing,         setIsRefreshing]         = useState(false);
  const [touchStart,           setTouchStart]           = useState<number | null>(null);
  const [touchEnd,             setTouchEnd]             = useState<number | null>(null);

  const searchInputRef     = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const contentRef         = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await initializeData();
      const saved = localStorage.getItem('recentTimetableSearches');
      if (saved) setRecentSearches(JSON.parse(saved));
      
      // Load saved filters
      const savedFilters = localStorage.getItem('browseFilters');
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          setSelectedCategory(filters.category || 'all');
          setSelectedProgram(filters.program || 'all');
          setSelectedTeacher(filters.teacher || 'all');
          setSelectedCourse(filters.course || 'all');
          setSelectedSemester(filters.semester || 'all');
          setSelectedGroup(filters.group || 'all');
        } catch {}
      }
      
      setIsLoading(false);
    };
    loadData();
  }, [initializeData]);

  // Apply search passed via navigation state
  useEffect(() => {
    if (location.state?.searchQuery && programs.length > 0) {
      const query = location.state.searchQuery;
      const type  = location.state.type;
      const allCourses  = Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.course_name || c.course))).filter(Boolean)));
      const allTeachers = Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.instructor || c.teacher))).filter(Boolean)));
      if (type === 'course') {
        const m = allCourses.find(c => c.toLowerCase() === query.toLowerCase());
        m ? setSelectedCourse(m) : setSearchQuery(query);
      } else if (type === 'teacher') {
        const m = allTeachers.find(t => t.toLowerCase() === query.toLowerCase());
        m ? setSelectedTeacher(m) : setSearchQuery(query);
      } else {
        const sl = query.toLowerCase().trim();
        const mt = allTeachers.find(t => t.toLowerCase() === sl);
        const mc = allCourses.find(c => c.toLowerCase() === sl);
        if (mt) setSelectedTeacher(mt);
        else if (mc) setSelectedCourse(mc);
        else setSearchQuery(query);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state, programs]);

  // Search suggestions
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const dp = DataProcessor.getInstance();
      const ql = searchQuery.toLowerCase();
      const out: any[] = [];
      programs.filter(p => p.program.toLowerCase().includes(ql)).slice(0, 2).forEach(p => {
        out.push({ type: 'program', text: p.program, description: `${p.department} · Sem ${p.semester}` });
      });
      dp.getTeachers().filter(t => t.toLowerCase().includes(ql)).slice(0, 2).forEach(t => {
        out.push({ type: 'teacher', text: t, description: 'Instructor' });
      });
      dp.getCourses().filter(c => c.toLowerCase().includes(ql)).slice(0, 2).forEach(c => {
        out.push({ type: 'course', text: c, description: 'Course' });
      });
      setSearchSuggestions(out);
    } else setSearchSuggestions([]);
  }, [searchQuery, programs]);

  // Click outside dismiss
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowSearchSuggestions(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Save filters to localStorage
  useEffect(() => {
    const filters = {
      category: selectedCategory,
      program: selectedProgram,
      teacher: selectedTeacher,
      course: selectedCourse,
      semester: selectedSemester,
      group: selectedGroup,
    };
    localStorage.setItem('browseFilters', JSON.stringify(filters));
  }, [selectedCategory, selectedProgram, selectedTeacher, selectedCourse, selectedSemester, selectedGroup]);

  // Swipe gesture handling for day navigation
  const minSwipeDistance = 50;
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (viewMode === 'day' && (isLeftSwipe || isRightSwipe)) {
      const { preferences } = useSettingsStore.getState();
      const days = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
      const currentIndex = days.indexOf(selectedDayView);
      
      if (isLeftSwipe && currentIndex < days.length - 1) {
        setSelectedDayView(days[currentIndex + 1]);
        triggerHaptic();
      } else if (isRightSwipe && currentIndex > 0) {
        setSelectedDayView(days[currentIndex - 1]);
        triggerHaptic();
      }
    }
  }, [touchStart, touchEnd, viewMode, selectedDayView]);

  // Haptic feedback helper
  const triggerHaptic = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  }, []);

  // Pull to refresh
  const handlePullRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    triggerHaptic();
    await initializeData();
    setTimeout(() => setIsRefreshing(false), 500);
  }, [isRefreshing, initializeData, triggerHaptic]);

  const handleSearchSelect = useCallback((text: string) => {
    setShowSearchSuggestions(false);
    if (text.trim()) {
      const r = [text, ...recentSearches.filter(s => s !== text)].slice(0, 5);
      setRecentSearches(r);
      localStorage.setItem('recentTimetableSearches', JSON.stringify(r));
    }
    const dp = DataProcessor.getInstance();
    const sl = text.toLowerCase().trim();
    const mt = dp.getTeachers().find(t => t.toLowerCase() === sl);
    if (mt) { setSelectedTeacher(mt); setSearchQuery(''); return; }
    const mc = dp.getCourses().find(c => c.toLowerCase() === sl);
    if (mc) { setSelectedCourse(mc); setSearchQuery(''); return; }
    const mp = Array.from(new Set(programs.map(p => p.program))).find(p => p.toLowerCase() === sl);
    if (mp) { setSelectedProgram(mp); setSearchQuery(''); return; }
    setSearchQuery(text);
  }, [recentSearches, programs]);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem('recentTimetableSearches');
  }, []);

  // Derived data (memoized for performance)
  const uniquePrograms = useMemo(
    () => Array.from(new Set(programs.map(p => p.program))).sort(),
    [programs]
  );
  const uniqueSemesters = useMemo(
    () => Array.from(new Set(programs.map(p => p.semester))).sort(),
    [programs]
  );
  const uniqueGroups = useMemo(
    () => Array.from(new Set(programs.map(p => p.group))).sort(),
    [programs]
  );
  const uniqueTeachers = useMemo(
    () => Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.instructor || c.teacher))).filter(Boolean))).sort(),
    [programs]
  );
  const uniqueCourses = useMemo(
    () => Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.course_name || c.course))).filter(Boolean))).sort(),
    [programs]
  );

  const filteredPrograms = useMemo(() => {
    return programs.filter(p => {
      const sl = searchQuery.toLowerCase().trim();
      const matchesSearch = searchQuery === '' || (() => {
        if (Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.course_name || c.course || '').toLowerCase() === sl))) return true;
        if (Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.instructor || c.teacher || '').toLowerCase() === sl))) return true;
        if (p.program.toLowerCase().includes(sl)) return true;
        if (p.department.toLowerCase().includes(sl)) return true;
        if (p.group.toLowerCase().includes(sl)) return true;
        if (Object.values(p.schedule).some((cs: any) => cs.some((c: any) => {
          const cn  = (c.course_name || c.course || '').toLowerCase();
          const ins = (c.instructor || c.teacher || '').toLowerCase();
          const loc = (c.location || '').toLowerCase();
          return cn.includes(sl) || ins.includes(sl) || loc.includes(sl);
        }))) return true;
        return false;
      })();
      const matchesCategory = selectedCategory === 'all' || (() => {
        const pl = p.program.toLowerCase();
        if (selectedCategory === 'bachelor') return pl.includes('bachelor') || pl.includes('bs ') || pl.includes('bba') || pl.includes('bcs');
        if (selectedCategory === 'master')   return pl.includes('master')   || pl.includes('ms ') || pl.includes('mba');
        return false;
      })();
      const matchesProgram  = selectedProgram  === 'all' || p.program  === selectedProgram;
      const matchesSemester = selectedSemester === 'all' || p.semester === selectedSemester;
      const matchesGroup    = selectedGroup    === 'all' || p.group    === selectedGroup;
      const matchesTeacher  = selectedTeacher  === 'all' || Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.instructor || c.teacher) === selectedTeacher));
      const matchesCourse   = selectedCourse   === 'all' || Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.course_name || c.course) === selectedCourse));
      return matchesSearch && matchesCategory && matchesProgram && matchesSemester && matchesGroup && matchesTeacher && matchesCourse;
    });
  }, [programs, searchQuery, selectedCategory, selectedProgram, selectedTeacher, selectedCourse, selectedSemester, selectedGroup]);

  const activeFilterCount = useMemo(
    () => [selectedCategory, selectedProgram, selectedTeacher, selectedCourse, selectedSemester, selectedGroup].filter(f => f !== 'all').length,
    [selectedCategory, selectedProgram, selectedTeacher, selectedCourse, selectedSemester, selectedGroup]
  );
  const clearAll = useCallback(() => {
    setSelectedCategory('all'); setSelectedProgram('all'); setSelectedTeacher('all');
    setSelectedCourse('all');   setSelectedSemester('all'); setSelectedGroup('all');
    setSearchQuery('');
    triggerHaptic();
  }, [triggerHaptic]);

  const unreadCount = dashboard ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length : 0;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="lg:hidden" style={{ background: T.bg, minHeight: '100vh', padding: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[80, 60, 200, 140, 140, 140].map((h, i) => (
            <div
              key={i}
              style={{
                height: h,
                background: T.surface,
                borderRadius: 12,
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.7; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className="lg:hidden pb-24"
      style={{ background: T.bg, fontFamily: T.sans, color: T.text, minHeight: '100vh' }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: `1px solid ${T.border}`,
        background: T.bg, position: 'sticky', top: 0, zIndex: 40,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <DesktopNav />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavIconBtn onClick={() => dashboard?.setShowSearch(true)}>
            <Search size={15} />
          </NavIconBtn>
          <NavIconBtn badge={unreadCount} onClick={() => {
            if (dashboard) {
              dashboard.setShowNotifications(!dashboard.showNotifications);
              if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100);
            }
          }}>
            <Bell size={15} />
          </NavIconBtn>
          <button
            onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
            style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: T.tealBg, border: `1px solid ${T.tealBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.teal, fontFamily: T.mono, fontSize: 11, fontWeight: 500, cursor: 'pointer',
            }}
          >{userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}</button>
        </div>
      </header>

      <div
        ref={contentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 24, position: 'relative' }}
      >
        {/* Pull to refresh indicator */}
        {isRefreshing && (
          <div style={{
            position: 'absolute',
            top: -40,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            background: T.tealBg,
            border: `1px solid ${T.tealBorder}`,
            borderRadius: 20,
            fontFamily: T.mono,
            fontSize: 9,
            letterSpacing: '.1em',
            color: T.teal,
            zIndex: 100,
          }}>
            <div style={{
              width: 12,
              height: 12,
              border: `2px solid ${T.teal}`,
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.6s linear infinite',
            }} />
            REFRESHING
          </div>
        )}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>

        {/* Pull to refresh trigger area */}
        <div
          onTouchStart={(e) => {
            if (contentRef.current && contentRef.current.scrollTop === 0) {
              const touch = e.touches[0];
              setTouchStart(touch.clientY);
            }
          }}
          onTouchMove={(e) => {
            if (touchStart && contentRef.current && contentRef.current.scrollTop === 0) {
              const touch = e.touches[0];
              const pullDistance = touch.clientY - touchStart;
              if (pullDistance > 80 && !isRefreshing) {
                handlePullRefresh();
                setTouchStart(null);
              }
            }
          }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1 }}
        />

        {/* ── Page title ───────────────────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontFamily: T.serif, fontSize: 26, fontWeight: 400, letterSpacing: '-.02em', margin: 0, lineHeight: 1 }}>
              Browse Timetables
            </h1>
            {filteredPrograms.length > 0 && (
              <span style={{
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', color: T.teal,
                background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                borderRadius: 4, padding: '2px 7px',
              }}>{filteredPrograms.length}</span>
            )}
          </div>
          <MonoLabel size={8}>Explore schedules across all departments</MonoLabel>
        </div>

        {/* ── Search bar ───────────────────────────────────────────────── */}
        <div ref={searchContainerRef} style={{ position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: T.surface,
            border: `1px solid ${searchFocused ? T.tealBorder : T.border}`,
            borderRadius: 12, padding: '0 14px',
            transition: 'border-color .2s',
            boxShadow: searchFocused ? `0 0 0 3px rgba(20,184,166,.08)` : 'none',
          }}>
            <Search size={15} color={searchFocused ? T.teal : T.text35} style={{ flexShrink: 0, transition: 'color .2s' }} />
            <input
              ref={searchInputRef}
              placeholder="Search course, instructor, room…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => { setSearchFocused(true); setShowSearchSuggestions(true); }}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={e => {
                if (e.key === 'Enter') { setShowSearchSuggestions(false); if (searchQuery.trim()) handleSearchSelect(searchQuery); }
                else if (e.key === 'Escape') { setShowSearchSuggestions(false); searchInputRef.current?.blur(); }
              }}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                color: T.text, fontFamily: T.sans, fontSize: 13, height: 46,
              }}
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setShowSearchSuggestions(false); searchInputRef.current?.focus(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text35, display: 'flex', alignItems: 'center', padding: 0 }}
              >
                <X size={14} />
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 10px', borderRadius: 7,
                background: showFilters || activeFilterCount > 0 ? T.tealBg : 'rgba(255,255,255,.05)',
                border: `1px solid ${showFilters || activeFilterCount > 0 ? T.tealBorder : T.border}`,
                color: showFilters || activeFilterCount > 0 ? T.teal : T.text50,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
                cursor: 'pointer', transition: '.2s', flexShrink: 0,
              }}
            >
              <SlidersHorizontal size={11} />
              {activeFilterCount > 0 ? (
                <span style={{ background: T.teal, color: T.bg, borderRadius: 3, padding: '1px 5px', fontSize: 8, fontWeight: 700 }}>
                  {activeFilterCount}
                </span>
              ) : (
                <span>FILTER</span>
              )}
              {showFilters ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          </div>

          {/* Search suggestions dropdown */}
          {showSearchSuggestions && (searchQuery.length > 0 || recentSearches.length > 0) && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
              background: '#141414', border: `1px solid ${T.borderHov}`,
              borderRadius: 12, zIndex: 50, overflow: 'hidden',
              boxShadow: '0 16px 40px rgba(0,0,0,.5)',
            }}>
              {searchQuery.length === 0 && recentSearches.length > 0 && (
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <MonoLabel size={8}>Recent</MonoLabel>
                    <button onClick={clearRecentSearches} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.mono, fontSize: 8, color: T.text35, letterSpacing: '.08em' }}>CLEAR</button>
                  </div>
                  {recentSearches.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => handleSearchSelect(s)}
                      style={{
                        width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 6px', borderRadius: 8, color: T.text50,
                        fontFamily: T.sans, fontSize: 12, textAlign: 'left',
                      }}
                    >
                      <History size={12} color={T.text25} />{s}
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.length > 0 && searchSuggestions.length > 0 && (
                <div style={{ padding: '14px 16px' }}>
                  <MonoLabel size={8}>Suggestions</MonoLabel>
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {searchSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => handleSearchSelect(s.text)}
                        style={{
                          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 8px', borderRadius: 8, textAlign: 'left',
                        }}
                      >
                        <div style={{
                          width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                          background: T.tealBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {s.type === 'teacher' ? <User size={12} color={T.teal} /> :
                           s.type === 'course'  ? <BookOpen size={12} color={T.teal} /> :
                                                  <TrendingUp size={12} color={T.teal} />}
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: T.text, fontFamily: T.sans }}>{s.text}</div>
                          <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, letterSpacing: '.06em', marginTop: 2 }}>
                            {s.description.toUpperCase()}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {searchQuery.length > 0 && searchSuggestions.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <MonoLabel color={T.text25}>No matches for "{searchQuery}"</MonoLabel>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Filter panel (collapsible) ───────────────────────────────── */}
        {showFilters && (
          <div style={{
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 14, padding: '18px 16px',
            display: 'flex', flexDirection: 'column', gap: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 400, letterSpacing: '-.01em' }}>
                Filters
              </span>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAll}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: T.mono, fontSize: 9, color: T.text35, letterSpacing: '.1em',
                  }}
                >
                  <RotateCcw size={9} /> RESET
                </button>
              )}
            </div>

            {/* Level (segmented) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <MonoLabel color={selectedCategory !== 'all' ? T.teal : T.text35}>Level</MonoLabel>
              <SegmentedFilter
                value={selectedCategory}
                onChange={setSelectedCategory}
                options={[
                  { v: 'all',      label: 'ALL'   },
                  { v: 'bachelor', label: "BACH'S" },
                  { v: 'master',   label: "MAST'S" },
                ]}
              />
            </div>

            {/* Program */}
            <FilterRow
              icon={LayoutGrid} label="Program"
              value={selectedProgram} options={uniquePrograms}
              onChange={setSelectedProgram}
              searchPlaceholder="Search program…"
            />
            {/* Teacher */}
            <FilterRow
              icon={Users} label="Instructor"
              value={selectedTeacher} options={uniqueTeachers}
              onChange={setSelectedTeacher}
              searchPlaceholder="Search instructor…"
            />
            {/* Course */}
            <FilterRow
              icon={BookOpen} label="Course"
              value={selectedCourse} options={uniqueCourses}
              onChange={setSelectedCourse}
              searchPlaceholder="Search course…"
            />

            {/* Semester + Group (side by side) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <FilterRow
                icon={Calendar} label="Semester"
                value={selectedSemester} options={uniqueSemesters}
                onChange={setSelectedSemester}
                searchPlaceholder="Find sem…"
              />
              <FilterRow
                icon={GraduationCap} label="Group"
                value={selectedGroup} options={uniqueGroups}
                onChange={setSelectedGroup}
                searchPlaceholder="Find group…"
              />
            </div>
          </div>
        )}

        {/* ── Active filter chips ──────────────────────────────────────── */}
        {activeFilterCount > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {selectedCategory !== 'all' && <ActiveChip label={selectedCategory === 'bachelor' ? "Bachelor's" : "Master's"} onRemove={() => setSelectedCategory('all')} />}
            {selectedProgram  !== 'all' && <ActiveChip label={selectedProgram.length > 22 ? selectedProgram.substring(0, 22) + '…' : selectedProgram} onRemove={() => setSelectedProgram('all')} />}
            {selectedTeacher  !== 'all' && <ActiveChip label={selectedTeacher.length > 22 ? selectedTeacher.substring(0, 22) + '…' : selectedTeacher} onRemove={() => setSelectedTeacher('all')} />}
            {selectedCourse   !== 'all' && <ActiveChip label={selectedCourse.length > 22 ? selectedCourse.substring(0, 22) + '…' : selectedCourse} onRemove={() => setSelectedCourse('all')} />}
            {selectedSemester !== 'all' && <ActiveChip label={`Sem ${selectedSemester}`} onRemove={() => setSelectedSemester('all')} />}
            {selectedGroup    !== 'all' && <ActiveChip label={`Group ${selectedGroup}`} onRemove={() => setSelectedGroup('all')} />}
          </div>
        )}

        {/* ── View mode toggle ─────────────────────────────────────────── */}
        <SegmentedFilter
          value={viewMode}
          onChange={v => setViewMode(v as 'day' | 'week')}
          options={[
            { v: 'day',  label: 'DAY VIEW'  },
            { v: 'week', label: 'WEEK VIEW' },
          ]}
        />

        {/* ── Day pills (day view only) ────────────────────────────────── */}
        {viewMode === 'day' && (
          <DayPills selectedDay={selectedDayView} onDayChange={setSelectedDayView} />
        )}

        {/* ── Schedule content ─────────────────────────────────────────── */}
        <ScheduleContent
          programs={filteredPrograms}
          viewMode={viewMode}
          selectedDay={selectedDayView}
          activeFilters={{ teacher: selectedTeacher, course: selectedCourse }}
          onViewDetails={p => { setSelectedTimetable(p); setShowDetails(true); }}
        />
      </div>

      {/* ── Details modal ──────────────────────────────────────────────── */}
      {showDetails && selectedTimetable && (
        <TimetableDetailsModal
          program={selectedTimetable}
          isOpen={showDetails}
          onClose={() => { setShowDetails(false); setSelectedTimetable(null); }}
          activeFilters={{ teacher: selectedTeacher, course: selectedCourse }}
        />
      )}
    </div>
  );
}

// ─── Day pills ────────────────────────────────────────────────────────────────
const DayPills = React.memo(({ selectedDay, onDayChange }: { selectedDay: string; onDayChange: (d: string) => void }) => {
  const { preferences } = useSettingsStore();
  const days  = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, marginTop: -8 }}>
      {days.map(day => {
        const isSelected = day === selectedDay;
        const isToday    = day === today;
        return (
          <button
            key={day}
            onClick={() => onDayChange(day)}
            style={{
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
              background: isSelected ? T.tealBg : T.surface,
              border: `1px solid ${isSelected ? T.tealBorder : T.border}`,
              color: isSelected ? T.teal : isToday ? 'rgba(255,255,255,.65)' : T.text50,
              fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
              whiteSpace: 'nowrap', transition: '.15s', position: 'relative', flexShrink: 0,
            }}
          >
            {day.substring(0, 3).toUpperCase()}
            {isToday && !isSelected && (
              <span style={{
                position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)',
                width: 3, height: 3, borderRadius: '50%', background: T.teal,
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
});

// ─── Schedule content (day or week view) ──────────────────────────────────────
function ScheduleContent({
  programs, viewMode, selectedDay, activeFilters, onViewDetails,
}: {
  programs: UnifiedProgram[]; viewMode: 'day' | 'week'; selectedDay: string;
  activeFilters?: { teacher: string; course: string };
  onViewDetails: (p: UnifiedProgram) => void;
}) {
  const { preferences } = useSettingsStore();
  const days  = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  const matches = (course: any) => {
    if (activeFilters?.teacher && activeFilters.teacher !== 'all' && (course.instructor || course.teacher) !== activeFilters.teacher) return false;
    if (activeFilters?.course  && activeFilters.course  !== 'all' && (course.course_name || course.course) !== activeFilters.course) return false;
    return true;
  };

  const getDayClasses = (day: string) => {
    const out: any[] = [];
    programs.forEach(p => {
      (p.schedule[day] || []).forEach((c: any) => {
        if (matches(c)) {
          out.push({
            time: c.time, course: c.course_name || c.course,
            room: c.location, instructor: c.instructor || c.teacher,
            program: p.program, semester: p.semester, group: p.group, programData: p,
          });
        }
      });
    });
    return out.sort((a, b) => compareTimeStrings(a.time, b.time));
  };

  if (viewMode === 'day') {
    const dayClasses = getDayClasses(selectedDay);
    return (
      <div>
        <SectionHeader
          title={selectedDay}
          meta={dayClasses.length > 0 ? `${dayClasses.length} ${dayClasses.length === 1 ? 'CLASS' : 'CLASSES'}` : undefined}
        />
        {dayClasses.length === 0 ? (
          <EmptyState text={`No classes on ${selectedDay}`} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dayClasses.map((cls, i) => (
              <MobileScheduleCard key={i} cls={cls} onClick={() => onViewDetails(cls.programData)} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Week view (mobile = vertical accordion of days)
  if (programs.length === 0) {
    return <EmptyState text="No timetables found" />;
  }
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {days.map(day => {
        const dayClasses = getDayClasses(day);
        const isToday    = day === today;
        return (
          <div key={day}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{
                  fontFamily: T.serif, fontSize: 16, fontWeight: 400, letterSpacing: '-.01em',
                  color: isToday ? T.teal : T.text,
                }}>{day}</span>
                {isToday && (
                  <MonoLabel color={T.teal} size={8}>TODAY</MonoLabel>
                )}
              </div>
              {dayClasses.length > 0 && (
                <MonoLabel size={8}>{dayClasses.length} {dayClasses.length === 1 ? 'CLASS' : 'CLASSES'}</MonoLabel>
              )}
            </div>
            {dayClasses.length === 0 ? (
              <div style={{
                background: T.surface, border: `1px dashed ${T.border}`,
                borderRadius: 10, padding: '14px 12px', textAlign: 'center',
              }}>
                <span style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, letterSpacing: '.1em' }}>NO CLASSES</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dayClasses.map((cls, i) => (
                  <MobileScheduleCard key={i} cls={cls} onClick={() => onViewDetails(cls.programData)} compact />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Mobile schedule card ─────────────────────────────────────────────────────
const MobileScheduleCard = React.memo(({ cls, onClick, compact }: { cls: any; onClick: () => void; compact?: boolean }) => {
  const [startTime] = cls.time.split('-');
  const endPart = cls.time.split('-')[1]?.trim() || '';

  return (
    <div
      onClick={onClick}
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderLeft: `2px solid ${T.teal}`,
        borderRadius: 12, padding: compact ? '12px 14px' : '14px 16px',
        display: 'flex', alignItems: 'flex-start', gap: 14,
        cursor: 'pointer', transition: '.15s',
      }}
    >
      {/* Time column */}
      <div style={{ minWidth: 50, textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: T.mono, fontSize: 11, color: T.teal, lineHeight: 1 }}>
          {startTime.trim()}
        </div>
        {endPart && (
          <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, marginTop: 3 }}>
            {endPart}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{
        width: 1, alignSelf: 'stretch', margin: '2px 0', flexShrink: 0,
        background: T.border,
      }} />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, lineHeight: 1.2, marginBottom: 4,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {cls.course}
        </div>
        <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, letterSpacing: '.07em', marginBottom: 8 }}>
          {cls.program} · SEM {cls.semester} · GRP {cls.group}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {cls.instructor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.text35 }}>
              <User size={10} color={T.teal} /> {cls.instructor}
            </div>
          )}
          {cls.room && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.text35 }}>
              <MapPin size={10} color={T.teal} /> {cls.room}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ text }: { text: string }) {
  return (
    <div style={{
      background: T.surface, border: `1px dashed ${T.border}`,
      borderRadius: 14, padding: '36px 24px', textAlign: 'center',
    }}>
      <Calendar size={24} color={T.text15} style={{ margin: '0 auto 10px', display: 'block' }} />
      <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', color: T.text25, margin: 0 }}>
        {text.toUpperCase()}
      </p>
    </div>
  );
}

// ─── Details modal ────────────────────────────────────────────────────────────
function TimetableDetailsModal({
  program, isOpen, onClose, activeFilters,
}: {
  program: UnifiedProgram; isOpen: boolean; onClose: () => void;
  activeFilters?: { teacher: string; course: string };
}) {
  const { preferences } = useSettingsStore();
  const allDays = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);

  const getFiltered = (ds: any[]) => {
    if (!ds) return [];
    let out = ds;
    if (activeFilters?.teacher && activeFilters.teacher !== 'all') out = out.filter((c: any) => (c.instructor || c.teacher) === activeFilters.teacher);
    if (activeFilters?.course  && activeFilters.course  !== 'all') out = out.filter((c: any) => (c.course_name || c.course) === activeFilters.course);
    return out;
  };

  const availableDays = allDays.filter(day => {
    const ds = program.schedule[day];
    if (!ds || ds.length === 0) return false;
    return ds.some((c: any) => {
      let m = true;
      if (activeFilters?.teacher && activeFilters.teacher !== 'all') m = m && (c.instructor || c.teacher) === activeFilters.teacher;
      if (activeFilters?.course  && activeFilters.course  !== 'all') m = m && (c.course_name || c.course) === activeFilters.course;
      return m;
    });
  });

  const [selectedDay, setSelectedDay] = useState(availableDays[0] ?? 'Monday');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="border-0 p-0"
        style={{
          background: '#111', width: '95vw', maxWidth: 500,
          maxHeight: '88vh', overflowY: 'auto',
          borderRadius: 16, border: `1px solid ${T.borderHov}`,
          boxShadow: '0 24px 64px rgba(0,0,0,.7)',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '20px 20px 16px',
          borderBottom: `1px solid ${T.border}`,
          background: 'linear-gradient(135deg, rgba(20,184,166,.07) 0%, transparent 60%)',
          position: 'relative',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -20, width: 140, height: 140, background: 'radial-gradient(circle, rgba(20,184,166,.12) 0%, transparent 65%)', pointerEvents: 'none' }} />
          <MonoLabel color={T.teal} size={8}>TIMETABLE DETAILS</MonoLabel>
          <h2 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, letterSpacing: '-.01em', margin: '8px 0 6px', lineHeight: 1.2 }}>
            {program.program}
          </h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[program.department, `Sem ${program.semester}`, `Group ${program.group}`].map(t => (
              <span key={t} style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', color: T.text35 }}>{t}</span>
            ))}
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 16, right: 16,
              width: 30, height: 30, borderRadius: 8,
              background: T.surface, border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.text50, cursor: 'pointer',
            }}
          ><X size={14} /></button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {availableDays.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
              {availableDays.map(day => (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  style={{
                    padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                    background: selectedDay === day ? T.tealBg : T.surface,
                    border: `1px solid ${selectedDay === day ? T.tealBorder : T.border}`,
                    color: selectedDay === day ? T.teal : T.text50,
                    fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
                    whiteSpace: 'nowrap', transition: '.15s',
                  }}
                >{day.substring(0, 3).toUpperCase()}</button>
              ))}
            </div>
          )}

          {availableDays.length > 0 && (() => {
            const courses = getFiltered(program.schedule[selectedDay]);
            return courses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {courses.map((course: any, i: number) => (
                  <div key={i} style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${T.teal}`,
                    borderRadius: 10, padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                      <h3 style={{ fontSize: 13, fontWeight: 500, color: T.text, margin: 0, lineHeight: 1.3, flex: 1 }}>
                        {course.course_name || course.course}
                      </h3>
                      <span style={{
                        fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em',
                        color: T.teal, background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                        borderRadius: 5, padding: '3px 8px', whiteSpace: 'nowrap', flexShrink: 0,
                      }}>{course.time}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      {[
                        { Icon: User,   text: course.instructor || course.teacher || 'TBA' },
                        { Icon: MapPin, text: course.location || 'TBA' },
                      ].map(({ Icon, text }) => (
                        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.text35 }}>
                          <Icon size={11} color={T.teal} /> {text}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text={`No matching classes on ${selectedDay}`} />
            );
          })()}

          {/* Weekly summary */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <MonoLabel size={8}>WEEKLY SUMMARY</MonoLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))', gap: 6, marginTop: 12 }}>
              {availableDays.map(day => {
                const count = getFiltered(program.schedule[day]).length;
                return (
                  <div key={day} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '8px 4px',
                    background: count > 0 ? T.tealBg : 'rgba(255,255,255,.02)',
                    border: `1px solid ${count > 0 ? T.tealBorder : T.border}`,
                    borderRadius: 8,
                  }}>
                    <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', color: count > 0 ? T.teal : T.text25 }}>
                      {day.substring(0, 3).toUpperCase()}
                    </span>
                    <span style={{ fontFamily: T.serif, fontSize: 16, color: count > 0 ? T.teal : T.text35, lineHeight: 1 }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}